"use client"

import clsx from "clsx"
import type { ReactNode } from "react"
import { Link } from "@/components/Link/Link.component"
import type { ArticleTeaserModel } from "@/services/api/content/models/article-teaser-model.types"
import type { SiteId } from "@/services/api/content/models/site.types"
import { Label } from "../Label/Label.component"
import { RequiresSubscriptionLabel } from "../RequiresSubscriptionLabel/RequiresSubscriptionLabel.component"
import { Translation } from "../Translation/Translation.component"

type SectionLinkProps = {
  readonly className?: string
  readonly children?: string | ReactNode
  readonly section: ArticleTeaserModel.Section | null
  readonly position?: string
  readonly type?: "default" | "primary" | "inverse"
  readonly isLocked?: boolean
  readonly siteId?: SiteId | null
  readonly isRecommendation?: boolean
  readonly size?: "xs" | "sm"
}

export function SectionLink({
  children,
  position,
  section,
  type = "primary",
  className,
  isLocked = false,
  siteId = null,
  isRecommendation = false,
  size,
}: SectionLinkProps) {
  const getSectionName = () => {
    if (isRecommendation) {
      return {
        da: "Fra arkivet",
        de: "Haben Sie gelesen?",
        en: "Did you read?",
        no: "Fikk du lest?",
        sv: "Läste du?",
      }
    }

    return section?.name ?? ""
  }

  const sectionObject = {
    name: getSectionName(),
    url: isRecommendation ? null : (section?.targetUrl ?? null),
  }

  return (
    <div className={clsx("flex flex-wrap items-center gap-y-0.5", className)}>
      {sectionObject.name !== "" ? (
        <Label
          className={siteId !== null ? `theme-${siteId}` : ""}
          size={size}
          type={isRecommendation ? "inverse" : type}
        >
          {sectionObject.url !== null ? (
            <Link
              className="w-fit cursor-pointer truncate leading-none hover:underline"
              href={sectionObject.url}
              position={position ?? "s-a"}
            >
              <Translation model={sectionObject.name} />
            </Link>
          ) : (
            <span className="truncate">
              <Translation model={sectionObject.name} />
            </span>
          )}
        </Label>
      ) : null}

      {isLocked ? (
        <RequiresSubscriptionLabel size={size} />
      ) : (
        (children ?? null)
      )}
    </div>
  )
}
